// client/src/components/MapPrivacyControls.js
import React, { useEffect, useMemo, useRef, useState } from "react";

export default function MapPrivacyControls({
  mapId,
  user,
  isOwner,
  isFounder,
  isPublic,
  setIsPublic,
  publishing,
  setPublishing,
  pubStatusMsg,
  setPubStatusMsg,
  seoApproved,
  setSeoApproved,
  seoUpdating,
  setSeoUpdating,
  seoStatusMsg,
  setSeoStatusMsg,
  isSeoIndexable,
  rightOffset = 18,
  onRefetchCommunityMap,
}) {
  const [allowAds, setAllowAds] = useState(false);
  const [adsUpdating, setAdsUpdating] = useState(false);
  const [adsStatusMsg, setAdsStatusMsg] = useState("");

  const [poiEditors, setPoiEditors] = useState([]);
  const [poiEditorsLoading, setPoiEditorsLoading] = useState(false);
  const [poiEditorInput, setPoiEditorInput] = useState("");
  const [poiEditorSelectedUser, setPoiEditorSelectedUser] = useState(null);
  const [poiEditorSearchResults, setPoiEditorSearchResults] = useState([]);
  const [poiEditorSearching, setPoiEditorSearching] = useState(false);
  const [poiEditorSaving, setPoiEditorSaving] = useState(false);
  const [poiEditorRemovingId, setPoiEditorRemovingId] = useState(null);
  const [poiEditorStatusMsg, setPoiEditorStatusMsg] = useState("");

  const [hoverAdsInfo, setHoverAdsInfo] = useState(false);
  const adsTooltipTimeoutRef = useRef(null);
  const searchAbortRef = useRef(null);

  const canManage = !!user && (isOwner || isFounder);

  // Allow opting in as soon as the map is Public.
  // Actual ad serving is gated elsewhere by (allowAds && seoApproved).
  const canToggleAds = !!isPublic;

  const apiFetch = (path, options) => fetch(path, { credentials: "include", ...(options || {}) });

  useEffect(() => {
    let ignore = false;

    (async () => {
      if (!mapId) return;
      try {
        const res = await apiFetch(`/api/maps/${mapId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (ignore) return;

        setAllowAds(!!data?.allow_ads);
      } catch {}
    })();

    return () => {
      ignore = true;
    };
  }, [mapId]);

  const loadPoiEditors = async () => {
    if (!canManage || !mapId) {
      setPoiEditors([]);
      return;
    }

    setPoiEditorsLoading(true);

    try {
      const res = await apiFetch(`/api/maps/${mapId}/poi-editors`);
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();
      setPoiEditors(Array.isArray(data) ? data : []);
    } catch {
      setPoiEditorStatusMsg("Failed to load POI editors.");
      setTimeout(() => setPoiEditorStatusMsg(""), 2500);
    } finally {
      setPoiEditorsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!canManage || !mapId) {
        setPoiEditors([]);
        return;
      }

      setPoiEditorsLoading(true);

      try {
        const res = await apiFetch(`/api/maps/${mapId}/poi-editors`);
        if (!res.ok) throw new Error("Failed");
        const data = await res.json();
        if (cancelled) return;
        setPoiEditors(Array.isArray(data) ? data : []);
      } catch {
        if (cancelled) return;
        setPoiEditorStatusMsg("Failed to load POI editors.");
        setTimeout(() => setPoiEditorStatusMsg(""), 2500);
      } finally {
        if (!cancelled) setPoiEditorsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canManage, mapId]);

  useEffect(() => {
    const q = String(poiEditorInput || "").trim();

    setPoiEditorSelectedUser((prev) => {
      if (!prev) return null;
      const label = String(prev.username || prev.email || prev.id || "");
      return label.toLowerCase() === q.toLowerCase() ? prev : null;
    });

    if (searchAbortRef.current) {
      try {
        searchAbortRef.current.abort();
      } catch {}
      searchAbortRef.current = null;
    }

    if (!canManage || q.length < 2) {
      setPoiEditorSearchResults([]);
      setPoiEditorSearching(false);
      return undefined;
    }

    const controller = new AbortController();
    searchAbortRef.current = controller;

    const timer = setTimeout(async () => {
      setPoiEditorSearching(true);

      try {
        const res = await fetch(`/api/user/search?q=${encodeURIComponent(q)}`, {
          credentials: "include",
          signal: controller.signal,
        });

        if (!res.ok) throw new Error("Failed");

        const data = await res.json();
        const users = Array.isArray(data?.users) ? data.users : [];

        setPoiEditorSearchResults(users);
      } catch (err) {
        if (err && err.name === "AbortError") return;
        setPoiEditorSearchResults([]);
      } finally {
        setPoiEditorSearching(false);
      }
    }, 180);

    return () => {
      clearTimeout(timer);
      try {
        controller.abort();
      } catch {}
    };
  }, [poiEditorInput, canManage]);

  const boxStyle = useMemo(
    () => ({
      position: "absolute",
      top: 12,
      right: rightOffset,
      zIndex: 1400,
      background: "rgba(16, 18, 22, 0.86)",
      border: "1px solid rgba(255,255,255,0.09)",
      boxShadow: "0 8px 22px rgba(0,0,0,0.42)",
      borderRadius: 10,
      padding: "8px 10px",
      minWidth: 250,
      maxWidth: 330,
      color: "#eaeaea",
      fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      backdropFilter: "blur(6px)",
      WebkitBackdropFilter: "blur(6px)",
    }),
    [rightOffset]
  );

  const titleStyle = {
    fontWeight: 900,
    fontSize: 12,
    letterSpacing: 0.3,
    marginBottom: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    padding: "6px 0",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  };

  const labelStyle = { display: "flex", alignItems: "center", gap: 8, fontWeight: 850, fontSize: 12 };

  const pillStyle = (on, disabled) => ({
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 950,
    letterSpacing: 0.2,
    border: "1px solid rgba(255,255,255,0.10)",
    background: disabled ? "rgba(70,70,70,0.32)" : on ? "rgba(255, 220, 94, 0.95)" : "rgba(35,35,35,0.92)",
    color: disabled ? "rgba(255,255,255,0.6)" : on ? "#232" : "#fff",
    cursor: disabled ? "not-allowed" : "pointer",
    userSelect: "none",
    lineHeight: 1,
    whiteSpace: "nowrap",
  });

  const smallStatusStyle = {
    marginTop: 6,
    fontSize: 11,
    color: "rgba(255,255,255,0.70)",
    lineHeight: 1.25,
    minHeight: 14,
  };

  const inputStyle = {
    flex: "1 1 auto",
    minWidth: 0,
    height: 28,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(0,0,0,0.28)",
    color: "#fff",
    padding: "0 8px",
    fontSize: 12,
    outline: "none",
  };

  const smallButtonStyle = (disabled) => ({
    height: 28,
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.14)",
    background: disabled ? "rgba(70,70,70,0.28)" : "rgba(255, 220, 94, 0.95)",
    color: disabled ? "rgba(255,255,255,0.55)" : "#232",
    fontSize: 11,
    fontWeight: 950,
    padding: "0 9px",
    cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
  });

  const removeButtonStyle = (disabled) => ({
    border: "1px solid rgba(255,255,255,0.12)",
    background: disabled ? "rgba(70,70,70,0.28)" : "rgba(255,80,80,0.16)",
    color: disabled ? "rgba(255,255,255,0.48)" : "rgba(255,190,190,0.95)",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 900,
    padding: "4px 7px",
    cursor: disabled ? "not-allowed" : "pointer",
  });

  const searchBoxStyle = {
    position: "absolute",
    left: 0,
    right: 48,
    top: 31,
    zIndex: 2600,
    background: "rgba(10, 12, 14, 0.98)",
    border: "1px solid rgba(255,255,255,0.14)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.55)",
    borderRadius: 9,
    overflow: "hidden",
    maxHeight: 165,
    overflowY: "auto",
  };

  const searchItemStyle = {
    width: "100%",
    display: "block",
    textAlign: "left",
    border: 0,
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    background: "transparent",
    color: "#fff",
    padding: "7px 9px",
    cursor: "pointer",
  };

  const tooltipWrapStyle = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
  };

  const infoDotStyle = (disabled) => ({
    width: 16,
    height: 16,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 950,
    background: disabled ? "rgba(70,70,70,0.32)" : "rgba(35,35,35,0.92)",
    border: "1px solid rgba(255,255,255,0.10)",
    color: disabled ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.88)",
    cursor: "default",
  });

  const tooltipStyle = {
    position: "absolute",
    right: 0,
    top: 22,
    width: 260,
    background: "rgba(10, 12, 14, 0.98)",
    border: "1px solid rgba(255,255,255,0.12)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.55)",
    borderRadius: 10,
    padding: "9px 10px",
    color: "rgba(255,255,255,0.92)",
    fontSize: 11,
    lineHeight: 1.25,
    zIndex: 2000,
  };

  const setPublic = async (next) => {
    if (!canManage) return;
    if (!mapId) return;

    setPublishing(true);
    setPubStatusMsg(next ? "Publishing…" : "Unpublishing…");

    try {
      const res = await apiFetch(`/api/maps/${mapId}/set-public`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_public: next ? 1 : 0 }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();

      setIsPublic(!!data?.is_public);

      // If your backend forces allow_ads off when a map is made private, keep UI in sync.
      if (typeof data?.allow_ads !== "undefined") {
        setAllowAds(!!data?.allow_ads);
      } else if (!next) {
        setAllowAds(false);
      }

      setPubStatusMsg(next ? "Map is now public." : "Map is now private.");
      if (typeof onRefetchCommunityMap === "function") {
        try {
          await onRefetchCommunityMap();
        } catch {}
      }
    } catch {
      setPubStatusMsg("Failed to update visibility.");
    } finally {
      setPublishing(false);
      setTimeout(() => setPubStatusMsg(""), 2500);
    }
  };

  const setSeoApprovedRemote = async (next) => {
    if (!isFounder) return;
    if (!mapId) return;

    setSeoUpdating(true);
    setSeoStatusMsg(next ? "Approving SEO…" : "Unapproving SEO…");

    try {
      const res = await apiFetch(`/api/maps/${mapId}/seo-approved`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ seo_approved: next ? 1 : 0 }),
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json();

      setSeoApproved(!!data?.seo_approved);

      setSeoStatusMsg(next ? "SEO approved." : "SEO approval removed.");
      if (typeof onRefetchCommunityMap === "function") {
        try {
          await onRefetchCommunityMap();
        } catch {}
      }
    } catch {
      setSeoStatusMsg("Failed to update SEO approval.");
    } finally {
      setSeoUpdating(false);
      setTimeout(() => setSeoStatusMsg(""), 2500);
    }
  };

  const setAllowAdsRemote = async (next) => {
    if (!canManage) return;
    if (!mapId) return;

    setAdsUpdating(true);
    setAdsStatusMsg(next ? "Enabling ads…" : "Disabling ads…");

    try {
      const res = await apiFetch(`/api/maps/${mapId}/set-allow-ads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allow_ads: next ? 1 : 0 }),
      });
      if (!res.ok) throw new Error("Failed");

      const data = await res.json();

      // Backend is source of truth.
      if (typeof data?.allow_ads !== "undefined") {
        setAllowAds(!!data?.allow_ads);
      } else {
        setAllowAds(!!next);
      }

      if (next) {
        if (!isPublic) {
          setAdsStatusMsg("Publish the map to enable ads.");
        } else if (!seoApproved) {
          setAdsStatusMsg("Ads enabled. They will start showing after SEO approval.");
        } else {
          setAdsStatusMsg("Ads enabled.");
        }
      } else {
        setAdsStatusMsg("Ads disabled.");
      }

      if (typeof onRefetchCommunityMap === "function") {
        try {
          await onRefetchCommunityMap();
        } catch {}
      }
    } catch {
      setAdsStatusMsg("Failed to update ads setting.");
    } finally {
      setAdsUpdating(false);
      setTimeout(() => setAdsStatusMsg(""), 3000);
    }
  };

  const selectPoiEditorUser = (nextUser) => {
    if (!nextUser) return;

    const label = String(nextUser.username || nextUser.email || nextUser.id || "").trim();
    setPoiEditorSelectedUser(nextUser);
    setPoiEditorInput(label);
    setPoiEditorSearchResults([]);
    setPoiEditorStatusMsg("");
  };

  const addPoiEditor = async () => {
    if (!canManage || !mapId) return;

    const q = String(poiEditorInput || "").trim();
    if (!q) {
      setPoiEditorStatusMsg("Search for a user first.");
      setTimeout(() => setPoiEditorStatusMsg(""), 2500);
      return;
    }

    let selectedUser = poiEditorSelectedUser;

    if (!selectedUser) {
      const exact = poiEditorSearchResults.find((item) => {
        const username = String(item.username || "").toLowerCase();
        const email = String(item.email || "").toLowerCase();
        return username === q.toLowerCase() || email === q.toLowerCase();
      });

      selectedUser = exact || null;
    }

    if (!selectedUser || !selectedUser.id) {
      if (poiEditorSearchResults.length > 0) {
        setPoiEditorStatusMsg("Select the user from the list first.");
      } else {
        setPoiEditorStatusMsg(poiEditorSearching ? "Still searching…" : "No matching user found.");
      }
      setTimeout(() => setPoiEditorStatusMsg(""), 3000);
      return;
    }

    setPoiEditorSaving(true);
    setPoiEditorStatusMsg("Adding editor…");

    try {
      const res = await apiFetch(`/api/maps/${mapId}/poi-editors`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedUser.id }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Failed");
      }

      setPoiEditorInput("");
      setPoiEditorSelectedUser(null);
      setPoiEditorSearchResults([]);
      setPoiEditorStatusMsg(data?.already_owner ? "That user already owns this map." : "POI editor added.");
      await loadPoiEditors();
    } catch (err) {
      setPoiEditorStatusMsg(err?.message || "Failed to add POI editor.");
    } finally {
      setPoiEditorSaving(false);
      setTimeout(() => setPoiEditorStatusMsg(""), 3000);
    }
  };

  const removePoiEditor = async (targetUserId) => {
    if (!canManage || !mapId) return;

    const uid = Number(targetUserId);
    if (!Number.isFinite(uid) || uid <= 0) return;

    setPoiEditorRemovingId(uid);
    setPoiEditorStatusMsg("Removing editor…");

    try {
      const res = await apiFetch(`/api/maps/${mapId}/poi-editors/${uid}`, {
        method: "DELETE",
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Failed");
      }

      setPoiEditors((prev) => prev.filter((x) => Number(x.user_id) !== uid));
      setPoiEditorStatusMsg("POI editor removed.");
    } catch (err) {
      setPoiEditorStatusMsg(err?.message || "Failed to remove POI editor.");
    } finally {
      setPoiEditorRemovingId(null);
      setTimeout(() => setPoiEditorStatusMsg(""), 3000);
    }
  };

  const adsTooltipText = (
    <>
      <div style={{ fontWeight: 950, marginBottom: 5 }}>Ads & revenue share</div>
      <div style={{ marginBottom: 6 }}>
        Revenue sharing is <span style={{ fontWeight: 950 }}>60%</span> to the map owner and{" "}
        <span style={{ fontWeight: 950 }}>40%</span> to the platform.
      </div>
      <div style={{ marginBottom: 6 }}>
        Ads are <span style={{ fontWeight: 950 }}>subject to approval</span>, based on map quality.
      </div>
      <div style={{ marginBottom: 6 }}>Payouts use the email associated with your Point Atlas account.</div>
      <div style={{ marginBottom: 7 }}>
        Ads only display when <span style={{ fontWeight: 950 }}>Enabled</span> and{" "}
        <span style={{ fontWeight: 950 }}>SEO Approved</span>.
      </div>
      <div>
        <a
          href="https://point-atlas.io/privacy"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "rgba(255, 220, 94, 0.95)",
            fontWeight: 950,
            textDecoration: "none",
            borderBottom: "1px solid rgba(255, 220, 94, 0.45)",
            paddingBottom: 1,
          }}
        >
          Privacy policy
        </a>
      </div>
    </>
  );

  const onAdsInfoEnter = () => {
    if (adsTooltipTimeoutRef.current) {
      clearTimeout(adsTooltipTimeoutRef.current);
      adsTooltipTimeoutRef.current = null;
    }
    setHoverAdsInfo(true);
  };

  const onAdsInfoLeave = () => {
    if (adsTooltipTimeoutRef.current) clearTimeout(adsTooltipTimeoutRef.current);
    adsTooltipTimeoutRef.current = setTimeout(() => {
      adsTooltipTimeoutRef.current = null;
      setHoverAdsInfo(false);
    }, 120);
  };

  if (!canManage) return null;

  const adsToggleDisabled = !canToggleAds || !!adsUpdating;
  const poiEditorAddDisabled = poiEditorSaving || !String(poiEditorInput || "").trim();
  const showSearchResults =
    String(poiEditorInput || "").trim().length >= 2 &&
    !poiEditorSelectedUser &&
    (poiEditorSearching || poiEditorSearchResults.length > 0);

  return (
    <div style={boxStyle}>
      <div style={titleStyle}>
        <div>Publishing</div>
        <div style={{ fontSize: 11, fontWeight: 850, color: isSeoIndexable ? "#ffdc5e" : "rgba(255,255,255,0.58)" }}>
          {isSeoIndexable ? "Indexable" : "Noindex"}
        </div>
      </div>

      <div style={{ ...rowStyle, borderTop: "none", paddingTop: 0 }}>
        <div style={labelStyle}>Public map</div>
        <div
          style={pillStyle(!!isPublic, !!publishing)}
          onClick={() => {
            if (publishing) return;
            setPublic(!isPublic);
          }}
          role="button"
          aria-label="Toggle public map"
        >
          {publishing ? "Working…" : isPublic ? "Public" : "Private"}
        </div>
      </div>

      <div style={rowStyle}>
        <div style={labelStyle}>SEO approved</div>
        <div
          style={pillStyle(!!seoApproved, !isFounder || !!seoUpdating)}
          onClick={() => {
            if (!isFounder) return;
            if (seoUpdating) return;
            setSeoApprovedRemote(!seoApproved);
          }}
          role="button"
          aria-label="Toggle SEO approved"
          title={!isFounder ? "Founder-only" : ""}
        >
          {seoUpdating ? "Working…" : seoApproved ? "Approved" : "Not approved"}
        </div>
      </div>

      <div style={rowStyle}>
        <div style={labelStyle}>
          Enable ads
          <span style={tooltipWrapStyle} onMouseEnter={onAdsInfoEnter} onMouseLeave={onAdsInfoLeave}>
            <span style={infoDotStyle(false)}>i</span>
            {hoverAdsInfo && <div style={tooltipStyle}>{adsTooltipText}</div>}
          </span>
        </div>

        <div
          style={pillStyle(!!allowAds, adsToggleDisabled)}
          onClick={() => {
            if (adsUpdating) return;

            if (!isPublic) {
              setAdsStatusMsg("To enable ads: publish the map first.");
              setTimeout(() => setAdsStatusMsg(""), 2500);
              return;
            }

            setAllowAdsRemote(!allowAds);
          }}
          role="button"
          aria-label="Toggle ads"
          title={!isPublic ? "Requires Public map" : !seoApproved ? "Ads can be enabled now; they display after SEO approval" : ""}
        >
          {adsUpdating ? "Working…" : allowAds ? "Enabled" : "Disabled"}
        </div>
      </div>

      <div style={{ borderTop: "1px solid rgba(255,255,255,0.09)", marginTop: 6, paddingTop: 9 }}>
        <div style={titleStyle}>
          <div>POI editors</div>
          <div style={{ fontSize: 11, fontWeight: 850, color: "rgba(255,255,255,0.58)" }}>
            {poiEditorsLoading ? "Loading…" : `${poiEditors.length}`}
          </div>
        </div>

        <div style={{ position: "relative", display: "flex", gap: 6, alignItems: "center" }}>
          <input
            value={poiEditorInput}
            onChange={(e) => {
              setPoiEditorInput(e.target.value);
              setPoiEditorSelectedUser(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();

                if (!poiEditorSelectedUser && poiEditorSearchResults.length === 1) {
                  selectPoiEditorUser(poiEditorSearchResults[0]);
                  return;
                }

                if (!poiEditorAddDisabled) addPoiEditor();
              }
            }}
            placeholder="Search username or email"
            style={inputStyle}
            disabled={poiEditorSaving}
          />

          <button
            type="button"
            style={smallButtonStyle(poiEditorAddDisabled)}
            disabled={poiEditorAddDisabled}
            onClick={addPoiEditor}
          >
            {poiEditorSaving ? "Adding…" : "Add"}
          </button>

          {showSearchResults && (
            <div style={searchBoxStyle}>
              {poiEditorSearching && (
                <div style={{ padding: "8px 9px", fontSize: 11, color: "rgba(255,255,255,0.65)" }}>
                  Searching…
                </div>
              )}

              {!poiEditorSearching &&
                poiEditorSearchResults.map((item) => {
                  const label = String(item.username || item.email || `User ${item.id}`);
                  const email = String(item.email || "").trim();

                  return (
                    <button
                      type="button"
                      key={item.id}
                      style={searchItemStyle}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectPoiEditorUser(item);
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 900 }}>{label}</div>
                      {!!email && email !== label && (
                        <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.58)", marginTop: 2 }}>
                          {email}
                        </div>
                      )}
                    </button>
                  );
                })}
            </div>
          )}
        </div>

        <div style={{ marginTop: 7, display: "flex", flexDirection: "column", gap: 5, maxHeight: 145, overflowY: "auto" }}>
          {!poiEditorsLoading && poiEditors.length === 0 && (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.3 }}>
              No extra POI editors yet.
            </div>
          )}

          {poiEditors.map((editor) => {
            const editorUserId = Number(editor.user_id);
            const label =
              String(editor.username || "").trim() ||
              String(editor.email || "").trim() ||
              `User ${editorUserId || ""}`;

            const subLabel =
              String(editor.email || "").trim() && String(editor.email || "").trim() !== label
                ? String(editor.email || "").trim()
                : editorUserId
                  ? `User ID ${editorUserId}`
                  : "";

            const removing = poiEditorRemovingId === editorUserId;

            return (
              <div
                key={`${editor.map_id || mapId}-${editorUserId}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  padding: "6px 7px",
                  borderRadius: 8,
                  background: "rgba(255,255,255,0.045)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {label}
                  </div>
                  {!!subLabel && (
                    <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.55)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {subLabel}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  style={removeButtonStyle(removing)}
                  disabled={removing}
                  onClick={() => removePoiEditor(editorUserId)}
                >
                  {removing ? "Removing…" : "Remove"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={smallStatusStyle}>{pubStatusMsg || seoStatusMsg || adsStatusMsg || poiEditorStatusMsg || ""}</div>
    </div>
  );
}
